import { Component, Input, OnInit, Output, EventEmitter, OnChanges, SimpleChanges, forwardRef } from '@angular/core';
import { ProductsService } from 'src/app/shared/services/products.service';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';

@Component({
  selector: 'app-product-tree-select',
  templateUrl: './product-tree-select.component.html',
  styleUrl: './product-tree-select.component.scss',
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => ProductTreeSelectComponent),
      multi: true
    }
  ]
})
export class ProductTreeSelectComponent implements OnInit, ControlValueAccessor {
  @Input() kind: string;
  @Input() metaKeySelection: boolean = false;
  @Input() selectionMode: 'single' | 'multiple' = 'multiple';

  // For form control and two-way binding
  @Input() selectedProductId: any = null;
  @Output() selectedProductIdChange = new EventEmitter<any>();

  // Add two-way binding for category, type, and item
  @Input() selectedCategory: string = null;
  @Output() selectedCategoryChange = new EventEmitter<string>();
  
  @Input() selectedType: string = null;
  @Output() selectedTypeChange = new EventEmitter<string>();
  
  @Input() selectedItem: string = null;
  @Output() selectedItemChange = new EventEmitter<string>();

  // For multiple selection only
  @Input() selectedProducts: any[] = [];
  @Output() selectedProductsChange = new EventEmitter<any[]>();
  @Output() selectionChanged = new EventEmitter<any>();
  
  treeNodes: any[] = [];
  selectedNodes: any[] = [];
  
  // Track previous selection to detect toggle behavior
  private previousSelectedNodes: any[] = [];

  // ControlValueAccessor implementation
  private onChange: any = () => {};
  private onTouched: any = () => {};

  constructor(private productsService: ProductsService) { }

  ngOnInit(): void {
    if (this.kind) {
      // Set default selection mode if not explicitly set
      if (!this.selectionMode) {
        this.selectionMode = 'multiple';
      }
      this.loadProductsTree();
    }
  }

  // Handle changes to inputs, particularly selectedProductId
  ngOnChanges(changes: SimpleChanges): void {
    if (changes.selectedProductId && !changes.selectedProductId.firstChange && this.treeNodes) {
      this.updateSelectedNodesFromId();
    }
  }

  loadProductsTree() {
    this.productsService.getProductsByKind(this.kind).subscribe(data => {
      this.treeNodes = this.transformToTreeNodes(data);
      if (this.selectedProductId) {
        this.updateSelectedNodesFromId();
      }
    });
  }

  private updateSelectedNodesFromId(): void {
    // Clear current selection
    this.selectedNodes = [];
    
    if (!this.selectedProductId) {
      return;
    }

    // Handle both single ID and array of IDs
    const ids = Array.isArray(this.selectedProductId) ? 
      this.selectedProductId : [this.selectedProductId];

    // Find and select nodes for each ID
    ids.forEach(id => {
      if (id) {
        this.findAndSelectNode(this.treeNodes, id);
      }
    });
    
    // Update the selectedProducts array to match the selection
    this.updateSelectedProductsFromNodes();
  }

  transformToTreeNodes(data: any): any[] {
    const treeNodes = [];
    let categoryIndex = 0;

    Object.keys(data).forEach(kind => {
      Object.keys(data[kind]).forEach(categoryName => {
        const categoryNode = {
          key: `${categoryIndex}`,
          label: categoryName,
          data: { 
            type: 'category', 
            name: categoryName,
            category: categoryName 
          },
          children: [],
          selectable: false
        };

        let typeIndex = 0;
        Object.keys(data[kind][categoryName]).forEach(typeName => {
          const typeNode = {
            key: `${categoryIndex}-${typeIndex}`,
            label: typeName,
            data: { 
              type: 'productType', 
              name: typeName,
              category: categoryName,
              productType: typeName
            },
            children: [],
            selectable: false
          };

          let itemIndex = 0;
          data[kind][categoryName][typeName].forEach(item => {
            // Normalize item data - ensure we always have a consistent structure
            let itemData;
            let itemValue;
            let productId;
            
            if (typeof item === 'string') {
              // Case: item is a simple string
              itemValue = item;
              itemData = { item: itemValue };
              productId = `${categoryName}-${typeName}-${itemValue}`.replace(/\s+/g, '_');
            } else if (item && typeof item === 'object') {
              // Case: item is an object
              itemData = { ...item }; // Clone to avoid reference issues
              
              // Extract the item value
              if ('item' in itemData && itemData.item) {
                itemValue = itemData.item;
              } else {
                // If no item property, use a sensible fallback
                itemValue = itemData.name || itemData.title || itemData.label || 
                           itemData.description || itemData.id || 
                           JSON.stringify(itemData).substring(0, 30);
              }
              
              // Use provided productId or generate one
              productId = itemData.productId || 
                         itemData.id || 
                         `${categoryName}-${typeName}-${itemValue}`.replace(/\s+/g, '_');
            } else {
              console.warn('Unexpected item type:', typeof item, item);
              return; // Skip this item
            }

            // Ensure the item has a proper value
            itemData.item = itemValue;
            
            // Create a clean label
            const label = this.formatItemLabel(itemData);
            
            const itemNode = {
              key: `${categoryIndex}-${typeIndex}-${itemIndex}`,
              label: label,
              data: { 
                type: 'item', 
                item: itemData,
                category: categoryName,
                productType: typeName,
                productId: productId,
                // Keep direct references for easier access
                itemValue: itemValue
              },
              selectable: true,
              leaf: true
            };
            
            // Validate the node data - ensure no undefined values
            if (!itemNode.data.category) itemNode.data.category = 'Unknown Category';
            if (!itemNode.data.productType) itemNode.data.productType = 'Unknown Type';
            if (!itemNode.data.itemValue) itemNode.data.itemValue = 'Unknown Item';
            if (!itemNode.data.productId) itemNode.data.productId = `node-${itemNode.key}`;
            
            typeNode.children.push(itemNode);
            itemIndex++;
          });

          if (typeNode.children.length > 0) {
            categoryNode.children.push(typeNode);
          }
          typeIndex++;
        });

        if (categoryNode.children.length > 0) {
          treeNodes.push(categoryNode);
        }
        categoryIndex++;
      });
    });

    return treeNodes;
  }

  formatItemLabel(item: any): string {
    return item.partNumber ? `${item.partNumber} - ${item.item}` : item.item;
  }

  writeValue(value: any): void {
    console.log('writeValue called with:', value);
    
    // Handle null/undefined/empty array values
    if (value === null || value === undefined || 
        (Array.isArray(value) && value.length === 0)) {
      this.clearSelection();
      return;
    }
    
    this.selectedProductId = value;
    
    if (this.treeNodes && this.treeNodes.length > 0) {
      this.updateSelectedNodesFromId();
    }
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    // Implementation for disabled state if needed
  }

  private findAndSelectNode(nodes: any[], productId: any): void {
    if (!productId) {
      console.warn('Attempted to find node with undefined/null productId');
      return;
    }
    
    for (const node of nodes) {
      // For leaf nodes (items)
      if (node.data?.type === 'item') {
        if (node.data?.productId === productId) {
          if (this.selectionMode === 'single') {
            this.selectedNodes = [node];
            return; // Stop after first match for single selection
          } else {
            // For multiple selection, add to array if not already there
            const exists = this.selectedNodes.some(n => n.key === node.key);
            if (!exists) {
              this.selectedNodes.push(node);
            }
          }
        }
      }
      
      // Continue searching in children
      if (node.children?.length > 0) {
        this.findAndSelectNode(node.children, productId);
      }
    }
  }

  // Handle direct node clicks for toggle behavior
  onNodeClick(node: any, event: Event): void {
    // Prevent the default tree selection behavior
    event.stopPropagation();
    
    if (node.data?.type !== 'item' || !node.selectable) {
      return;
    }
    
    const isCurrentlySelected = this.selectedNodes.some(selectedNode => 
      selectedNode.key === node.key
    );
    
    if (this.selectionMode === 'single') {
      if (isCurrentlySelected) {
        // Deselect if already selected
        this.selectedNodes = [];
      } else {
        // Select the node
        this.selectedNodes = [node];
      }
    } else {
      // Multiple selection mode
      if (isCurrentlySelected) {
        // Remove from selection
        this.selectedNodes = this.selectedNodes.filter(selectedNode => 
          selectedNode.key !== node.key
        );
      } else {
        // Add to selection
        this.selectedNodes = [...this.selectedNodes, node];
      }
    }
    
    this.onSelectionChange();
  }

  // Handle tree click events
  onTreeClick(event: any): void {
    // This method can be used for additional tree-level click handling if needed
  }

  // Handle node selection with toggle behavior
  onNodeSelect(event: any): void {
    // This might not be needed anymore with direct click handling
    // but keeping it for compatibility
    setTimeout(() => {
      this.onSelectionChange();
    }, 0);
  }

  // Handle node unselection
  onNodeUnselect(event: any): void {
    setTimeout(() => {
      this.onSelectionChange();
    }, 0);
  }
  
  // Update the previous selection tracking
  private updatePreviousSelection(): void {
    this.previousSelectedNodes = [...this.selectedNodes];
  }

  onSelectionChange() {
    // Update previous selection at the start
    this.updatePreviousSelection();
    
    if (!this.selectedNodes || this.selectedNodes.length === 0) {
      this.clearSelection();
      return;
    }
    
    const selections = this.selectedNodes
      .filter(node => node?.data?.type === 'item')
      .map(node => {
        // Log the node we're processing
        this.logNodeData(node, 'Processing selection: ');
        
        const nodeData = node.data;
        if (!nodeData) {
          console.warn('Node missing data:', node);
          return null;
        }

        // Get item data - could be a string or an object
        const itemData = nodeData.item;
        let itemValue;
        
        if (typeof itemData === 'string') {
          itemValue = itemData;
        } else if (itemData && typeof itemData === 'object') {
          itemValue = itemData.item || JSON.stringify(itemData).substring(0, 30);
        } else {
          itemValue = nodeData.itemValue || 'Unknown Item';
        }

        // Extract properly formatted values
        const categoryValue = nodeData.category || 'Unknown Category';
        const productTypeValue = nodeData.productType || 'Unknown Type';

        // Ensure we have a productId
        const productId = nodeData.productId || 
          `${categoryValue}-${productTypeValue}-${itemValue}`.replace(/\s+/g, '_');

        // Construct a valid selection object with guaranteed non-undefined values
        const selection = {
          category: { 
            category: categoryValue
          },
          productType: { 
            productType: productTypeValue
          },
          item: { 
            item: itemValue
          },
          productId: productId
        };
        
        return selection;
      })
      .filter(selection => selection !== null && 
              selection.category?.category && 
              selection.productType?.productType && 
              selection.item?.item);

   if (this.selectionMode === 'single') {
      const selection = selections[0] || null;
      if (selection) {
        // Update product ID
        this.selectedProductId = selection.productId;
        this.selectedProductIdChange.emit(this.selectedProductId);
        this.onChange(this.selectedProductId);
        
        // Update category, type, and item with two-way binding
        this.selectedCategory = selection.category.category;
        this.selectedCategoryChange.emit(this.selectedCategory);
        
        this.selectedType = selection.productType.productType;
        this.selectedTypeChange.emit(this.selectedType);
        
        this.selectedItem = selection.item.item;
        this.selectedItemChange.emit(this.selectedItem);
        
        this.selectionChanged.emit(selection);
      } else {
        this.clearSelection();
      }
    } else {
      // For multiple selection
      if (selections.length > 0) {
        const selectedProductIds = selections.map(s => s.productId).filter(id => id != null);
        this.selectedProductId = selectedProductIds;
        this.selectedProductIdChange.emit(selectedProductIds);
        this.onChange(selectedProductIds);
        
        // Update the selected products array
        this.selectedProducts = selections;
        this.selectedProductsChange.emit(this.selectedProducts);
        
        // For multiple selection, use first item for the individual properties
        const firstSelection = selections[0];
        if (firstSelection) {
          this.selectedCategory = firstSelection.category.category;
          this.selectedCategoryChange.emit(this.selectedCategory);
          
          this.selectedType = firstSelection.productType.productType;
          this.selectedTypeChange.emit(this.selectedType);
          
          this.selectedItem = firstSelection.item.item;
          this.selectedItemChange.emit(this.selectedItem);
        }
        
        // Emit the array of selections
        this.selectionChanged.emit(selections);
      } else {
        this.clearSelection();
      }
    }
    
    this.onTouched();
  }

  clearSelection(): void {
    this.selectedNodes = [];
    this.selectedProducts = [];
    this.selectedProductId = null;
    
    // Clear and emit category, type, and item
    this.selectedCategory = null;
    this.selectedType = null;
    this.selectedItem = null;
    
    // Emit all changes
    this.selectedProductIdChange.emit(this.selectedProductId);
    this.selectedProductsChange.emit(this.selectedProducts);
    this.selectedCategoryChange.emit(this.selectedCategory);
    this.selectedTypeChange.emit(this.selectedType);
    this.selectedItemChange.emit(this.selectedItem);
    
    this.selectionChanged.emit(this.selectionMode === 'single' ? null : []);
  }

  // Debug method to trace selection data
  private logNodeData(node: any, prefix: string = ''): void {
    if (!node) {
      console.warn(`${prefix}Node is null or undefined`);
      return;
    }
    
    const data = node.data || {};
    const itemData = data.item || {};
  }

  private updateSelectedProductsFromNodes(): void {
    if (!this.selectedNodes || this.selectedNodes.length === 0) {
      this.selectedProducts = [];
      return;
    }

    // Use the same logic as in onSelectionChange to create selection objects
    this.selectedProducts = this.selectedNodes
      .filter(node => node?.data?.type === 'item')
      .map(node => {
        const nodeData = node.data;
        if (!nodeData) return null;

        // Get item value
        const itemData = nodeData.item;
        let itemValue;
        
        if (typeof itemData === 'string') {
          itemValue = itemData;
        } else if (itemData && typeof itemData === 'object') {
          itemValue = itemData.item || JSON.stringify(itemData).substring(0, 30);
        } else {
          itemValue = nodeData.itemValue || 'Unknown Item';
        }

        return {
          category: { 
            category: nodeData.category || 'Unknown Category'
          },
          productType: { 
            productType: nodeData.productType || 'Unknown Type'
          },
          item: { 
            item: itemValue
          },
          productId: nodeData.productId || `unknown-${Date.now()}`
        };
      })
      .filter(selection => selection !== null);
      
    // Emit the updated selections
    this.selectedProductsChange.emit(this.selectedProducts);
  }
}